/**
 * Flow Message Service
 *
 * Handles AI responses for Flow conversations within the messaging system.
 * Flow appears as a regular user in conversations but responds with AI.
 *
 * Extended for full AI agent integration: tool approval messages, typing
 * indicators, token usage tracking, and audit logging.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { getLogger } from '@kit/shared/logger';
import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database, Json } from '@kit/supabase/database';

import { BALLEE_USER_ID, isBalleeUser } from '../../lib/ballee-constants';

/** Metadata shape for tool-approval messages */
export interface ToolApprovalMetadata {
  ai_tool_approval: {
    approval_id: string;
    tool_name: string;
    tool_args: Record<string, unknown>;
    risk_level: number;
    action_summary: string;
    status: 'pending' | 'approved' | 'rejected' | 'expired';
  };
}

/** Metadata shape for tool-result messages */
export interface ToolResultMetadata {
  ai_tool_result: {
    approval_id: string;
    tool_name: string;
    success: boolean;
    result_summary: string;
    error?: string;
  };
}

export class BalleeMessageService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Check if a conversation includes Flow as a participant
   */
  async isBalleeConversation(conversationId: string): Promise<Result<boolean>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await fetchAllRows((from, to) =>
      this.client
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conversationId)
        .eq('is_active', true)
        .order('id')
        .range(from, to),
    );

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const hasBallee = (data || []).some((p) => isBalleeUser(p.user_id));
    return Result.ok(hasBallee);
  }

  /**
   * Get conversation history for Flow AI context
   * Returns last N messages formatted for AI consumption
   */
  async getConversationHistory(
    conversationId: string,
    limit: number = 20,
  ): Promise<Result<Array<{ role: 'user' | 'assistant'; content: string }>>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('messages')
      .select('sender_id, content')
      .eq('conversation_id', conversationId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    // Reverse to get chronological order, then map to AI format
    const messages = (data || []).reverse().map((msg) => ({
      role: isBalleeUser(msg.sender_id)
        ? ('assistant' as const)
        : ('user' as const),
      content: msg.content || '',
    }));

    return Result.ok(messages);
  }

  /**
   * Insert a message from Flow into a conversation
   * Uses service role to bypass RLS since Flow is a system user
   */
  async sendAsBallee(
    conversationId: string,
    content: string,
  ): Promise<Result<{ id: string }>> {
    const logger = await getLogger();
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    if (!content || content.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Message content is required'),
      );
    }

    logger.info(
      {
        name: 'ballee.sendMessage',
        conversationId,
        contentLength: content.length,
      },
      'Sending Flow message',
    );

    // Insert message from Flow's user ID
    // RLS policy allows service_role to insert as Flow
    const { data, error } = await this.client
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: BALLEE_USER_ID,
        content: content.trim(),
        content_type: 'text',
        attachments: [],
      })
      .select('id')
      .single();

    if (error) {
      logger.error(
        {
          name: 'ballee.sendMessage.error',
          conversationId,
          error: error.message,
        },
        'Failed to send Flow message',
      );
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok({ id: data.id });
  }

  /**
   * Get the current user's context for Flow AI
   * Similar to ChatbotService.getUserContext but for messaging context
   */
  async getUserContext(userId: string): Promise<
    Result<{
      userId: string;
      accountId: string | null;
      isSuperAdmin: boolean;
      profile: {
        firstName: string | null;
        lastName: string | null;
        email: string | null;
      } | null;
    }>
  > {
    const uuidValidation = this.validateUUID(userId, 'userId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    // Get profile info
    const { data: profile, error: profileError } = await this.client
      .from('profiles')
      .select('id, first_name, last_name')
      .eq('id', userId)
      .single();

    if (profileError) {
      return Result.fail(this.handleDatabaseError(profileError));
    }

    // Check super admin status
    const { data: isSuperAdmin } = await this.client.rpc('is_super_admin');

    // Get user email
    const {
      data: { user },
    } = await this.client.auth.getUser();

    return Result.ok({
      userId,
      accountId: userId, // Personal account ID is the same as user ID
      isSuperAdmin: !!isSuperAdmin,
      profile: profile
        ? {
            firstName: profile.first_name,
            lastName: profile.last_name,
            email: user?.email || null,
          }
        : null,
    });
  }

  /**
   * Get full user context for AI tool execution (roles, permissions, isSuperAdmin).
   * Mirrors ChatbotService.getUserContext but works from the conversation module.
   */
  async getFullUserContext(userId: string): Promise<
    Result<{
      userId: string;
      accountId: string;
      roles: string[];
      permissions: string[];
      isSuperAdmin: boolean;
    }>
  > {
    const uuidValidation = this.validateUUID(userId, 'userId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data: isSuperAdmin } = await this.client.rpc('is_super_admin');

    const { data: membership, error: membershipError } = await this.client
      .from('accounts_memberships')
      .select('account_id, account_role')
      .eq('user_id', userId)
      .single();

    if (membershipError && !isSuperAdmin) {
      return Result.fail(
        new ServiceError(
          'NO_ACCOUNT',
          'User has no account membership',
          membershipError,
        ),
      );
    }

    const accountId = membership?.account_id || userId;
    const accountRole = membership?.account_role || 'owner';

    let permissions: string[] = [];
    if (membership) {
      const { data: rolePermissions } = await fetchAllRows((from, to) =>
        this.client
          .from('role_permissions')
          .select('permission')
          .eq('role', accountRole)
          .order('id')
          .range(from, to),
      );
      permissions = rolePermissions?.map((p) => p.permission) || [];
    }

    if (isSuperAdmin) {
      permissions = ['*'];
    }

    return Result.ok({
      userId,
      accountId,
      roles: [accountRole],
      permissions,
      isSuperAdmin: !!isSuperAdmin,
    });
  }

  /**
   * Insert a tool-approval message into the conversation.
   * These messages render as inline approval cards in the UI.
   */
  async sendToolApprovalMessage(
    conversationId: string,
    approval: ToolApprovalMetadata['ai_tool_approval'],
  ): Promise<Result<{ id: string }>> {
    const logger = await getLogger();

    const { data, error } = await this.client
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: BALLEE_USER_ID,
        content: `Tool approval required: **${approval.action_summary}**`,
        content_type: 'tool-approval',
        metadata: { ai_tool_approval: approval } as unknown as Json,
        attachments: [],
      })
      .select('id')
      .single();

    if (error) {
      logger.error(
        {
          name: 'ballee.toolApproval.error',
          conversationId,
          error: error.message,
        },
        'Failed to send tool approval message',
      );
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok({ id: data.id });
  }

  /**
   * Insert a tool-result message into the conversation.
   */
  async sendToolResultMessage(
    conversationId: string,
    result: ToolResultMetadata['ai_tool_result'],
  ): Promise<Result<{ id: string }>> {
    const logger = await getLogger();

    const content = result.success
      ? `Tool executed: **${result.result_summary}**`
      : `Tool failed: ${result.error || 'Unknown error'}`;

    const { data, error } = await this.client
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: BALLEE_USER_ID,
        content,
        content_type: 'tool-result',
        metadata: { ai_tool_result: result } as unknown as Json,
        attachments: [],
      })
      .select('id')
      .single();

    if (error) {
      logger.error(
        {
          name: 'ballee.toolResult.error',
          conversationId,
          error: error.message,
        },
        'Failed to send tool result message',
      );
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok({ id: data.id });
  }

  /**
   * Update the status of a tool-approval message's metadata.
   */
  async updateToolApprovalStatus(
    messageId: string,
    status: 'approved' | 'rejected' | 'expired',
  ): Promise<Result<void>> {
    // Fetch current metadata
    const { data: msg, error: fetchError } = await this.client
      .from('messages')
      .select('metadata')
      .eq('id', messageId)
      .single();

    if (fetchError || !msg) {
      return Result.fail(new ServiceError('NOT_FOUND', 'Message not found'));
    }

    const metadata = (msg.metadata || {}) as Record<string, unknown>;
    const approval = metadata.ai_tool_approval as
      | ToolApprovalMetadata['ai_tool_approval']
      | undefined;

    if (!approval) {
      return Result.fail(
        new ServiceError('INVALID', 'Message is not a tool approval'),
      );
    }

    const { error } = await this.client
      .from('messages')
      .update({
        metadata: {
          ...metadata,
          ai_tool_approval: { ...approval, status },
        } as unknown as Json,
      })
      .eq('id', messageId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Broadcast a typing indicator via Supabase Realtime.
   */
  async broadcastTyping(
    conversationId: string,
    isTyping: boolean,
  ): Promise<void> {
    try {
      const channel = this.client.channel(`conversation:${conversationId}`);
      await channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: { user_id: BALLEE_USER_ID, is_typing: isTyping },
      });
      // Unsubscribe immediately — we only needed to send
      this.client.removeChannel(channel);
    } catch {
      // Non-critical — don't fail the request
    }
  }

  /**
   * Broadcast tool execution progress via Supabase Realtime.
   * Shows users what Flow is currently doing (e.g. "Searching dancers...").
   */
  async broadcastToolProgress(
    conversationId: string,
    toolName: string,
    status: 'running' | 'completed',
  ): Promise<void> {
    try {
      const channel = this.client.channel(`conversation:${conversationId}`);
      await channel.send({
        type: 'broadcast',
        event: 'tool_progress',
        payload: {
          user_id: BALLEE_USER_ID,
          tool_name: toolName,
          status,
        },
      });
      this.client.removeChannel(channel);
    } catch {
      // Non-critical — don't fail the request
    }
  }

  /**
   * Record token usage in ai_conversation_context (upsert).
   */
  async logTokenUsage(
    conversationId: string,
    usage: { inputTokens: number; outputTokens: number },
  ): Promise<void> {
    const logger = await getLogger();

    try {
      // Upsert: create or increment counters (select + update/insert).
      const { data: ctx } = await this.client
        .from('ai_conversation_context')
        .select('id, total_input_tokens, total_output_tokens, ai_message_count')
        .eq('conversation_id', conversationId)
        .maybeSingle();

      if (ctx) {
        await this.client
          .from('ai_conversation_context')
          .update({
            total_input_tokens: ctx.total_input_tokens + usage.inputTokens,
            total_output_tokens: ctx.total_output_tokens + usage.outputTokens,
            ai_message_count: ctx.ai_message_count + 1,
            last_ai_interaction_at: new Date().toISOString(),
          })
          .eq('conversation_id', conversationId);
      } else {
        await this.client.from('ai_conversation_context').insert({
          conversation_id: conversationId,
          total_input_tokens: usage.inputTokens,
          total_output_tokens: usage.outputTokens,
          ai_message_count: 1,
          last_ai_interaction_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      logger.warn(
        { name: 'ballee.tokenUsage.error', conversationId, error },
        'Failed to log token usage (non-critical)',
      );
    }
  }

  /**
   * Log an audit event to chatbot_audit_logs with flow_conversation_id.
   */
  async logAudit(
    userId: string,
    accountId: string,
    conversationId: string,
    eventType: string,
    details?: { actionName?: string; result?: string; error?: string },
  ): Promise<void> {
    try {
      await this.client.from('chatbot_audit_logs').insert({
        user_id: userId,
        account_id: accountId,
        conversation_id: null,
        flow_conversation_id: conversationId,
        event_type: eventType,
        action_name: details?.actionName || null,
        result: details?.result || null,
        error_message: details?.error || null,
      });
    } catch {
      // Non-critical
    }
  }
}
